// services/ProvidersService.js
import axios from "axios";
import { getConfig } from "../configService";

let _cityStateCache = null;

export class ProvidersService {
  static keycloak = null;

  static getProvidersServiceBaseUrl(config) {
    if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && getConfig().localhostApi) {
      return "http://localhost:5046";
    }
    return config.apiBaseUrl + "/providers";
  }

  static async getCityStateList() {
    if (_cityStateCache) return _cityStateCache;
    const config = getConfig();
    const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);
    const response = await axios.get(`${providersServiceBaseUrl}/1.0/provider/cities`);
    _cityStateCache = response.data;
    return _cityStateCache;
  }

  static async getCityCenter(city, state) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);
      const response = await axios(`${providersServiceBaseUrl}/search/city-center?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`);
      return response.data; // { latitude, longitude, radiusMiles, providerCount }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async searchProviders(latitude, longitude, distance, category, keywords, pageNumber, pageSize, city, state, bounds) {
    try {
      // Get config at call-time
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      // FIRST priority: bounds mode
      if (bounds && bounds.minLatitude != null && bounds.maxLatitude != null && bounds.minLongitude != null && bounds.maxLongitude != null) {
        let url = `${providersServiceBaseUrl}/search?minLatitude=${bounds.minLatitude}&maxLatitude=${bounds.maxLatitude}&minLongitude=${bounds.minLongitude}&maxLongitude=${bounds.maxLongitude}&keywords=${encodeURIComponent(keywords || "")}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
        if (category) url += `&category=${encodeURIComponent(category)}`;
        const response = await axios(url);
        return response.data;
      }

      // If city/state are both set, use that path. Otherwise use geo.
      const hasCityState = city && state;
      let url;
      if (hasCityState) {
        url = `${providersServiceBaseUrl}/search?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&keywords=${encodeURIComponent(keywords || "")}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
        if (category) url += `&category=${encodeURIComponent(category)}`;
      } else {
        if (!latitude || !longitude || !distance) {
          console.error("required search parameters are undefined");
          return;
        }
        url = `${providersServiceBaseUrl}/search?latitude=${latitude}&longitude=${longitude}&distance=${distance}&keywords=${encodeURIComponent(keywords || "")}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
        if (category) url += `&category=${encodeURIComponent(category)}`;
      }
      const response = await axios(url);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Bearer header for the current Keycloak session, if there is one. Anonymous visitors
   * get no header. The token is refreshed first; if that fails the request goes out
   * without the header, so the caller is treated as anonymous.
   */
  static async getOptionalAuthHeaders() {
    const kc = ProvidersService.keycloak;
    if (!kc?.authenticated) {
      return {};
    }
    try {
      await kc.updateToken(30);
    } catch {
      return {};
    }
    return { Authorization: `Bearer ${kc.token}` };
  }

  static async getSingleProvider(id) {
    const config = getConfig();
    const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

    const response = await axios({
      method: "get",
      url: `${providersServiceBaseUrl}/1.0/provider/${id}`,
      headers: await this.getOptionalAuthHeaders(),
    });
    if (response.status !== 200) {
      throw Error(`Could not get provider ${id}. Status code ${response.status} - ${response.statusText}`);
    }
    return response.data;
  }

  static async getSingleProviderByFriendlyUrl(friendlyUrl) {
    const config = getConfig();
    const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

    const response = await axios({
      method: "get",
      url: `${providersServiceBaseUrl}/1.0/provider/by-url/${encodeURIComponent(friendlyUrl)}`,
      headers: await this.getOptionalAuthHeaders(),
    });
    if (response.status !== 200) {
      throw Error(`Could not get provider ${friendlyUrl}. Status code ${response.status} - ${response.statusText}`);
    }
    return response.data;
  }

  /**
   * Exchanges a Cloudflare Turnstile token (action "reveal-contact") for the business phone
   * and email. Anonymous endpoint. Errors propagate so callers can read error.response.status
   * (403 = verification failed, 503 = verification unavailable, 404 = provider missing).
   */
  static async revealContact(providerId, token) {
    const config = getConfig();
    const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

    const response = await axios({
      method: "post",
      url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/contact`,
      data: { token },
      headers: {
        "Content-Type": "application/json"
      },
    });
    return response.data;
  }

  static async addProviderReview(providerId, review, keycloakInstance = null) {
    const config = getConfig();
    const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);
    const kc = keycloakInstance || ProvidersService.keycloak;
    if (!kc || !kc.token) {
      throw new Error("addProviderReview called without a Keycloak token");
    }

    const response = await axios({
      method: "post",
      url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/review`,
      data: review,
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + kc.token
      },
    });
    return response.data;
  }

  /**
   * Returns the provider record for the currently authenticated user.
   * Resolves claimed providers (invitation flow) and legacy self-registered providers alike.
   *
   * Accepts an optional keycloak instance because this method is called from Header.jsx
   * which renders on public routes before PrivateRoute has a chance to set
   * ProvidersService.keycloak.
   */
  static async getMyProvider(keycloakInstance = null) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const kc = keycloakInstance || ProvidersService.keycloak;
      if (!kc || !kc.token) {
        throw new Error("getMyProvider called without a Keycloak token");
      }

      const response = await axios({
        url: `${providersServiceBaseUrl}/1.0/provider/me`,
        method: 'GET',
        headers: {
          "Authorization": "Bearer " + kc.token
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Returns ALL provider records claimed by the currently authenticated user.
   * Backs the top-level business-selector UI for accounts that have claimed
   * more than one crawled business via separate invitation links. Always
   * returns an array (possibly empty).
   *
   * Accepts an optional keycloak instance, same shape as getMyProvider, so
   * components rendering on public routes can still call this before
   * PrivateRoute has had a chance to set ProvidersService.keycloak.
   */
  static async getMyProviders(keycloakInstance = null) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const kc = keycloakInstance || ProvidersService.keycloak;
      if (!kc || !kc.token) {
        throw new Error("getMyProviders called without a Keycloak token");
      }

      const response = await axios({
        url: `${providersServiceBaseUrl}/1.0/provider/mine`,
        method: 'GET',
        headers: {
          "Authorization": "Bearer " + kc.token
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getStripeAccountLink(id, redirectUrl) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        url: `${providersServiceBaseUrl}/1.0/provider/${id}/stripe/account/link?redirectUrl=${redirectUrl}`,
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async createStripeAccount(id) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${id}/stripe/account/create`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async getStripeAccountStatus(id) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        url: `${providersServiceBaseUrl}/1.0/provider/${id}/stripe/account/status`,
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async MoveImage(providerId, imageId, newIndex) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
        url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/imageList/${imageId}/moveToIndex/${newIndex}`,
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async claimProvider(invitation) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/claim`,
        data: { invitation },
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async updateProvider(provider) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${provider.id}`,
        data: provider,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async generateDescriptions(providerId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/generate-descriptions`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async updateProviderThumbnail(id, imageId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "put",
        url: `${providersServiceBaseUrl}/1.0/provider/${id}/thumbnail`,
        data: imageId,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async generateUploadURL(id) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/photoUploader/${id}`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async uploadPhotoToStorage(uploadURL, image) {
    try {
      // This call doesn't rely on config, so we just do it directly:
      return await axios({
        method: "put",
        url: uploadURL,
        data: image,
        headers: {
          "Content-Type": "image/jpeg",
          "x-ms-blob-type": "BlockBlob",
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async completeUploadJob(id, imageId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "put",
        data: {
          status: "Completed",
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
        url: `${providersServiceBaseUrl}/1.0/photoUploader/${id}/${imageId}/status`,
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async deleteImage(subscriberId, imageId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "delete",
        url: `${providersServiceBaseUrl}/1.0/provider/${subscriberId}/imageList/${imageId}`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async generateProviderImage(providerId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/PhotoUploader/${providerId}/generate-image`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
      return response.data; // { imageId, status }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async generateSlotThumbnailUploadURL(providerId, slotId) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/photoUploader/${providerId}/slot/${slotId}/thumbnail`,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async completeSlotThumbnailUpload(providerId, slotId, thumbnailGuid) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      return await axios({
        method: "put",
        url: `${providersServiceBaseUrl}/1.0/photoUploader/${providerId}/slot/${slotId}/thumbnail/${thumbnailGuid}/status`,
        data: {
          status: "Completed",
        },
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async requestProviderClaim(providerId, { businessName, email, phone }) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/claim-request`,
        data: { businessName, email, phone },
        headers: {
          "Content-Type": "application/json"
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return error.response;
    }
  }

  static async requestClaimCall(providerId, phone) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/claim-call`,
        data: { phone },
        headers: {
          "Content-Type": "application/json"
        },
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(error);
      return error.response || { status: 0, data: null };
    }
  }

  static async verifyClaimCall(providerId, code) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/provider/${providerId}/claim-call/verify`,
        data: { code },
        headers: {
          "Content-Type": "application/json"
        },
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(error);
      return error.response || { status: 0, data: null };
    }
  }

  static async checkDuplicateProvider(data) {
    try {
      const config = getConfig();
      const providersServiceBaseUrl = this.getProvidersServiceBaseUrl(config);

      const response = await axios({
        method: "post",
        url: `${providersServiceBaseUrl}/1.0/Provider/duplicate-check`,
        data,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + ProvidersService.keycloak.token
        },
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      console.error(error);
      return error.response || { status: 0, data: null };
    }
  }
}
